"use client";

import React, { useEffect } from "react";
import { toast } from "sonner";
import { Send, Paperclip } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function InitiateCollaboration() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="95823" id="inquire-for-acquisition" className="dark bg-background text-foreground relative min-h-screen overflow-hidden py-24 lg:py-32 flex items-center">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[80vw] h-[80vw] max-w-[800px] max-h-[800px] bg-primary/5 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2 mix-blend-screen" />
        <div className="absolute bottom-0 right-0 w-[80vw] h-[80vw] max-w-[800px] max-h-[800px] bg-primary/10 rounded-full blur-[120px] translate-x-1/3 translate-y-1/3 mix-blend-screen" />
        {}
        <div className="absolute inset-0 opacity-[0.03] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIj48ZmlsdGVyIGlkPSJuIj48ZmVUdXJidWxlbmNlIHR5cGU9ImZyYWN0YWxOb2lzZSIgYmFzZUZyZXF1ZW5jeT0iMC44IiBudW1PY3RhdmVzPSIzIiBzdGl0Y2hUaWxlcz0ic3RpdGNoIi8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMDAlSIgZmlsdGVyPSJ1cmwoI24pIi8+PC9zdmc+')] mix-blend-overlay" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-8 items-start">
          
          {}
          <div ref={textRef} className={`lg:col-span-5 lg:sticky lg:top-32 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <h2 className="text-5xl md:text-[6vw] font-default font-bold tracking-tighter leading-[0.9] uppercase text-transparent bg-clip-text bg-gradient-to-b from-foreground to-foreground/50 mb-6">
              Strategic<br />Asset<br />Acquisition
            </h2>
            <p className="text-muted-foreground text-lg md:text-xl max-w-md font-default tracking-tight">
              Submit your venture parameters. Our investment committee evaluates high-authority infrastructure and scalable software holdings for potential integration.
            </p>
          </div>

          {}
          <div className="lg:col-span-7 lg:col-start-6">
            <div className="backdrop-blur-sm bg-card/5 border border-white/5 p-8 md:p-12 rounded-sm shadow-2xl">
              <FormProvider formKey="acquisition_inquiry_form_home" formId="acquisition_inquiry_form_home" sectionName="Inquire for Acquisition" defaultValues={{
              name: "",
              email: "",
              company: "",
              project_brief: "",
              budget: "",
              timeline: "",
              attachment: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              project_brief: {
                required: true,
                minLength: 10
              }
            }} submitText="Initialize Evaluation" successMessage="Transmission received. Our strategic committee will evaluate the dossier.">
                <CollaborationForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
      
      <Toaster position="bottom-right" />
    </section>;
}
function CollaborationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success("Dossier Transmitted", {
        description: successMessage,
        duration: 5000,
        icon: <Send className="size-4 text-primary" />
      });
    }
  }, [isSubmitted, submitError, successMessage]);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-none bg-primary/20 flex items-center justify-center mb-4">
          <Send className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-bold tracking-tight">Evaluation Initiated</h3>
        <p className="text-muted-foreground max-w-md font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-white/10 hover:bg-white/5 rounded-none font-mono uppercase tracking-widest text-xs">
          Submit New Inquiry
        </Button>
      </div>;
  }
  const minimalistInputClasses = "flex w-full bg-transparent border-0 border-b border-border/50 rounded-none px-0 py-3 text-lg md:text-xl shadow-none focus-visible:ring-0 focus-visible:border-primary transition-colors placeholder:text-muted-foreground/30 font-default";
  const minimalistLabelClasses = "text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1 block";
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <FormField control={control} name="name" rules={{
        required: "Principal name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={minimalistLabelClasses}>Principal Contact *</FormLabel>
              <FormControl>
                <Input placeholder="Alex Mercer" className={minimalistInputClasses} {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-mono uppercase tracking-wider opacity-50">Full legal name</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Institutional email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={minimalistLabelClasses}>Institutional Email *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="a.mercer@entity.com" className={minimalistInputClasses} {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-mono uppercase tracking-wider opacity-50">Secure communication channel</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="company" render={({
      field
    }) => <FormItem>
            <FormLabel className={minimalistLabelClasses}>Entity / Holding Name</FormLabel>
            <FormControl>
              <Input placeholder="Global Infrastructure Ltd" className={minimalistInputClasses} {...field} />
            </FormControl>
            <FormDescription className="text-[10px] font-mono uppercase tracking-wider opacity-50">Target or parent entity</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_brief" rules={{
      required: "Venture overview is required",
      minLength: {
        value: 10,
        message: "Please provide more detail"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={minimalistLabelClasses}>Venture Overview *</FormLabel>
            <FormControl>
              <textarea placeholder="Describe the asset, market position, and core technology stack..." className={`${minimalistInputClasses} min-h-[120px] resize-y outline-none`} {...field} />
            </FormControl>
            <FormDescription className="text-[10px] font-mono uppercase tracking-wider opacity-50">Include primary KPIs and unique intellectual property value.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel className={minimalistLabelClasses}>Valuation Range</FormLabel>
              <FormControl>
                <select className={`${minimalistInputClasses} appearance-none cursor-pointer outline-none`} {...field}>
                  <option value="" disabled className="bg-card text-muted-foreground">Select a range</option>
                  <option value="Under $5M" className="bg-card text-foreground">Under $5M</option>
                  <option value="$5M - $25M" className="bg-card text-foreground">$5M - $25M</option>
                  <option value="$25M - $100M" className="bg-card text-foreground">$25M - $100M</option>
                  <option value="Over $100M" className="bg-card text-foreground">Over $100M</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="timeline" render={({
        field
      }) => <FormItem>
              <FormLabel className={minimalistLabelClasses}>Proposed Integration Date</FormLabel>
              <FormControl>
                <Input type="date" className={`${minimalistInputClasses} [&::-webkit-calendar-picker-indicator]:filter-[invert(1)] [&::-webkit-calendar-picker-indicator]:opacity-50 cursor-pointer`} {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-mono uppercase tracking-wider opacity-50">Target date for closing or transition.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="attachment" render={({
      field
    }) => <FormItem>
            <FormLabel className={minimalistLabelClasses}>Financial Documentation (optional)</FormLabel>
            <FormControl>
              <div className="relative">
                <Input type="file" className={`${minimalistInputClasses} file:hidden text-transparent cursor-pointer`} {...field} onChange={e => {
            field.onChange(e);
          }} />
                <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none text-muted-foreground">
                  <Paperclip className="size-5 mr-2 opacity-50" />
                  <span className="text-lg font-default">{field.value ? 'Dossier Attached' : 'Choose file...'}</span>
                </div>
              </div>
            </FormControl>
            <FormDescription className="text-[10px] font-mono uppercase tracking-wider opacity-50">Upload pitch decks, P&L statements, or technical specs (max 10 MB).</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm font-mono">
          System Error: {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-16 mt-8 rounded-none bg-primary text-primary-foreground text-lg font-default font-bold tracking-widest uppercase relative overflow-hidden group transition-all hover:bg-primary/90">
        <span className="relative z-10 flex items-center justify-center gap-3">
          {isSubmitting ? 'Processing Dossier...' : 'Submit Acquisition Inquiry'}
          {!isSubmitting && <Send className="size-5 group-hover:translate-x-1 transition-transform" />}
        </span>
        {}
        <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:animate-[shimmer_1.5s_infinite] transition-transform duration-1000 ease-in-out group-hover:translate-x-full" />
      </Button>
    </Form>;
}